import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserIcon, LogOutIcon, CreditCardIcon, 
  MailIcon, KeyIcon, CheckCircleIcon
} from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { createCustomerPortalSession } from '../lib/stripe';
import Footer from '../components/common/Footer';

const Account: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentPlan, plans, userFlags } = useSubscription();
  
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleManageSubscription = async () => {
    try {
      console.log('Opening customer portal...');
      await createCustomerPortalSession();
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      // More user-friendly error handling
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('No Stripe customer')) {
        alert('No subscription found. Please upgrade to a paid plan first.');
      } else {
        alert('Failed to open subscription management. Please try again or contact support.');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    
    setPasswordError('');
    
    if (newPassword.length < 6) {
      return setPasswordError('Password must be at least 6 characters');
    }
    
    if (newPassword !== confirmPassword) {
      return setPasswordError('Passwords do not match');
    }
    
    // Mock password change
    setTimeout(() => {
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setPasswordSuccess(false);
        setIsChangingPassword(false);
      }, 3000);
    }, 1000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-gray-50 py-8"
    >
      <div className="max-w-3xl mx-auto px-4">
      <motion.div variants={itemVariants} className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Account Settings</h1>
            <p className="text-lg text-gray-600 mt-2">
              Manage your profile and preferences
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Card className="mb-8 p-6">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center mb-6">
            <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
            Profile Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="flex">
                <div className="flex-grow">
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    leftIcon={<MailIcon className="h-5 w-5 text-gray-400" />}
                  />
                </div>
                <Button
                  variant="outline"
                  className="ml-4 mt-1 whitespace-nowrap"
                  onClick={() => {}}
                  disabled
                >
                  Change Email
                </Button>
              </div>
            </div>
            
            {isChangingPassword ? (
              <form onSubmit={handlePasswordChange}>
                <div className="space-y-4 border p-4 rounded-md bg-gray-50">
                  {passwordSuccess && (
                    <div className="bg-success-50 border-l-4 border-success-500 p-3 rounded-md">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-success-500 mr-2" />
                        <p className="text-success-700">Password updated successfully!</p>
                      </div>
                    </div>
                  )}
                  
                  {passwordError && (
                    <div className="bg-error-50 border-l-4 border-error-500 p-3 rounded-md">
                      <p className="text-error-700">{passwordError}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      leftIcon={<KeyIcon className="h-5 w-5 text-gray-400" />}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      leftIcon={<KeyIcon className="h-5 w-5 text-gray-400" />}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      leftIcon={<KeyIcon className="h-5 w-5 text-gray-400" />}
                    />
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      type="submit"
                      variant="primary"
                      className="flex-1"
                    >
                      Update Password
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordError('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="flex">
                  <div className="flex-grow">
                    <Input
                      type="password"
                      value="••••••••"
                      disabled
                      leftIcon={<KeyIcon className="h-5 w-5 text-gray-400" />}
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="ml-4 mt-1 whitespace-nowrap"
                    onClick={() => setIsChangingPassword(true)}
                  >
                    Change Password
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Card className="mb-8 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <CreditCardIcon className="h-5 w-5 mr-2 text-primary-600" />
                Current Plan: {plans[currentPlan].name}
                {userFlags?.plan_override ? (
                  <div className="ml-2 flex items-center">
                    <span className="text-sm bg-accent-100 text-accent-800 px-3 py-1 rounded-full flex items-center">
                      <span className="mr-1">✨</span>
                      Complimentary Pro Access
                    </span>
                    {userFlags.is_beta && (
                      <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-3 py-1 rounded-full">
                        Beta User
                      </span>
                    )}
                  </div>
                ) : null}
              </h2>
              <p className="text-gray-600 mt-1">
                {userFlags?.plan_override 
                  ? 'You have unlimited access to all premium features'
                  : currentPlan === 'free' 
                    ? 'Free plan with limited features' 
                    : `$${plans[currentPlan].price}/month`}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0">
              {!userFlags?.plan_override && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/subscription')}
                >
                  Upgrade Plan
                </Button>
              )}
              {currentPlan !== 'free' && !userFlags?.plan_override && (
                <Button
                  variant="outline"
                  onClick={handleManageSubscription}
                  leftIcon={<CreditCardIcon className="h-4 w-4" />}
                >
                  Manage Subscription
                </Button>
              )}
            </div>
          </div>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center mb-6">
            <LogOutIcon className="h-5 w-5 mr-2 text-primary-600" />
            Account Actions
          </h2>
          
          <div className="space-y-4">
            <Button
              variant="outline"
              className="w-full border-gray-300 text-gray-700"
              onClick={handleLogout}
              leftIcon={<LogOutIcon className="h-5 w-5" />}
            >
              Sign Out
            </Button>
            
            <div className="pt-2">
              <button className="text-sm text-error-600 hover:text-error-800">
                Delete account
              </button>
              <p className="text-xs text-gray-500 mt-1">
                This action cannot be undone. All your data will be permanently deleted.
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
      
      <Footer />
      </div>
    </motion.div>
  );
};

export default Account;